import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Spinner, Alert, Form } from "react-bootstrap";
import axios from "axios";
import LabelFieldComponent from "../../components/fields/LabelFieldComponent";
import PageLayout from "../../layouts/PageLayout";
import MplacerProductsTableComponent from "../../components/tables/Mplacer/MplacedProductsTableComponent";

export default function MplacedProductList() {
    const { t } = useContext(TranslatorContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");

    const tableHeaders = [
        "Product Code",
        "Title",
        "Category",
        "Brand",
        "Selling Style",
        "Buying Price",
        "Quantity",
        "Marketplaces",
        "Action",
    ];

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/market-placer/market-placed/products`,
                { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
            );
            if (res.data?.success) setAllProducts(res.data.data || []);
            else setError(res.data?.message || "Failed to load products.");
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch products.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    const categories = useMemo(() => {
        const list = [], map = new Map();
        allProducts.forEach((p) => {
            const id = p.category?.id || p.category_id;
            if (id && p.category?.name && !map.has(id)) {
                map.set(id, true);
                list.push({ id, name: p.category.name });
            }
        });
        return list;
    }, [allProducts]);

    const filteredProducts = useMemo(() => {
        return allProducts.filter((p) => {
            const matchesSearch = !search ||
                p.title?.toLowerCase().includes(search.toLowerCase()) ||
                p.product_code?.toLowerCase().includes(search.toLowerCase());
            const matchesCategory = !selectedCategory || String(p.category?.id || p.category_id) === String(selectedCategory);
            return matchesSearch && matchesCategory;
        });
    }, [allProducts, search, selectedCategory]);

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Market Placed Products ({filteredProducts.length})</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="#" className="mc-breadcrumb-link">{t("home")}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to="#" className="mc-breadcrumb-link">{t("products")}</Link></li>
                                <li className="mc-breadcrumb-item">Market Placed</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
                        <Row className="g-3 align-items-end">
                            <Col md={6} xl={4}>
                                <LabelFieldComponent
                                    type="search"
                                    placeholder="Search by Code or Title..."
                                    label="Search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </Col>
                            <Col md={6} xl={4}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">Category</Form.Label>
                                    <Form.Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} style={{ height: "45px" }}>
                                        <option value="">All Categories</option>
                                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col xl={12} className="mt-4">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-2 text-muted">Loading products...</p>
                                    </div>
                                ) : (
                                    <MplacerProductsTableComponent thead={tableHeaders} tbody={filteredProducts} />
                                )}
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}